import React from 'react';
import clsx from 'clsx';

import styles from '../../card-listing/Card.module.scss';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { CardFields } from '../CardListingShared';
import {
  AspectRatioClassMap,
  ImageAspectRatio,
} from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { ContentGridCardAlignment } from '../ContentGrid/ContentGrid';
import { CtaFields } from 'lib/types/headlineFields';

export interface GeneralCardProps extends CtaFields {
  fieldsToShow: CardFields[];
  contentImage: React.ReactNode | null;
  imageLabel: React.ReactNode | null;
  showLabel?: boolean;
  eyebrow: React.ReactNode | null;
  title: React.ReactNode | null;
  description: React.ReactNode | null;
  gridAspectRatio?: ImageAspectRatio;
  alignment?: ContentGridCardAlignment;
  cardURL?: string;
  cardTarget?: string;
}

export const GeneralCardComponent = (props: GeneralCardProps) => {
  const {
    contentImage,
    imageLabel,
    eyebrow,
    title,
    description,
    cardURL,
    cardTarget,
    fieldsToShow,
    gridAspectRatio = '3:2',
    alignment = 'LeftAlign',
    cta1,
    cta2,
  } = props;
  if (
    contentImage === null &&
    imageLabel === null &&
    eyebrow === null &&
    title === null &&
    description === null &&
    !cardURL &&
    !cta1 &&
    !cta2
  ) {
    return null;
  }

  const showImage = fieldsToShow.includes('Image') && contentImage;
  const showBody =
    (fieldsToShow.includes('Eyebrow') && eyebrow) ||
    (fieldsToShow.includes('Title') && title) ||
    (fieldsToShow.includes('Description') && description);

  const hasLink = !!cardURL;

  return (
    <div
      className={clsx(
        styles.card,
        { [styles.card_Center]: alignment === 'CenterAlign' },
        { [styles.nonInteractive]: !hasLink }
      )}
    >
      <div className={styles.cardContent}>
        {showImage && (
          <div className={clsx(styles.cardImageWrapper, AspectRatioClassMap[gridAspectRatio])}>
            {fieldsToShow.includes('ImageLabel') && imageLabel && props.showLabel !== false && (
              <div className={styles.cardLabelWrapper}>
                <div className={styles.cardLabel}>{imageLabel}</div>
              </div>
            )}
            <div className={clsx(styles.cardImage)}>{contentImage}</div>
          </div>
        )}

        {showBody && (
          <div className={clsx(styles.cardBody)}>
            <div className={styles.cardBodyCopy}>
              {fieldsToShow.includes('Eyebrow') && (
                <div className={clsx(styles.cardBodyEyebrow)}>{eyebrow}</div>
              )}
              <Title
                fieldsToShow={fieldsToShow}
                url={props.cardURL}
                urlTarget={cardTarget}
                title={props.title}
              />
              <Description fieldsToShow={fieldsToShow} description={description} />
            </div>
          </div>
        )}
      </div>
      <CtaWrapper cta1={props.cta1} cta2={props.cta2} />
    </div>
  );
};

function Title(props: {
  fieldsToShow: CardFields[];
  url: string | undefined;
  urlTarget?: string | undefined;
  title: React.ReactNode | null;
}) {
  const { fieldsToShow, url, urlTarget, title } = props;
  const target = urlTarget || undefined;
  if (!fieldsToShow.includes('Title') || title === null) {
    return null;
  }
  if (url) {
    return (
      <h3 className={styles.cardBodyTitle}>
        <CtaLink
          url={url}
          target={target}
          suppressNewTabIcon={true}
          className={styles.cardBodyTitleFullCardLink}
          ctaStyle="none"
        >
          {title}
        </CtaLink>
      </h3>
    );
  }
  return <h3 className={styles.cardBodyTitle}>{title}</h3>;
}

function Description(props: { fieldsToShow: CardFields[]; description: React.ReactNode | null }) {
  const { fieldsToShow, description } = props;
  if (!fieldsToShow.includes('Description') || description === null) {
    return null;
  }
  // Forcing Primary theme for description to ensure link colors are correct, otherwise it can show up as white on white.
  // We don't have on-surface tokens for RTE links, so this is a workaround to ensure the correct colors are applied.
  return <div className={clsx(styles.cardBodyDescription, 'ThemesPrimary')}>{description}</div>;
}

function CtaWrapper(props: CtaFields) {
  const { cta1, cta2 } = props;
  if (!cta1 && !cta2) {
    return null;
  }
  return (
    <div className={clsx(styles.cardBodyCtaWrapper)}>
      {props.cta1 && props.cta1}
      {props.cta2 && props.cta2}
    </div>
  );
}
